import {
  getPetaCampaignAttribution,
  PETA_CAMPAIGN_ID,
  PETA_CAMPAIGN_IN_PERSON_ID,
  PETA_CAMPAIGN_QA_ID,
  type PetaCampaignAccess,
  type PetaCampaignEvaluation,
  type PetaCampaignId,
} from './types'

export const PETA_CAMPAIGN_COOKIE = 'peta_campaign_access'
export const PETA_CAMPAIGN_QUERY_PARAM = 'campaign_access'
export const PETA_CAMPAIGN_HANDOFF_PARAM = 'campaign_handoff'

const CAMPAIGN_COOKIE_LIFETIME_MS = 24 * 60 * 60 * 1000
const encoder = new TextEncoder()

export type PetaCampaignRuntimeConfig = {
  enabled: boolean
  accessKey?: string
  inPersonAccessKey?: string
  qaAccessKey?: string
  startIso?: string
  endIso?: string
  inPersonEndIso?: string
  allowedHosts: string[]
}

type CampaignCookiePayload = {
  campaignId: PetaCampaignId
  expiresAt: number
}

function normalizeHost(host: string) {
  return host.trim().toLowerCase().split(':')[0]
}

function parseDate(value: string | undefined) {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isFinite(parsed.getTime()) ? parsed : null
}

function constantTimeEqual(left: string, right: string) {
  const leftBytes = encoder.encode(left)
  const rightBytes = encoder.encode(right)
  const length = Math.max(leftBytes.length, rightBytes.length)
  let mismatch = leftBytes.length ^ rightBytes.length

  for (let index = 0; index < length; index += 1) {
    mismatch |= (leftBytes[index] ?? 0) ^ (rightBytes[index] ?? 0)
  }

  return mismatch === 0
}

function getSigningKey(config: PetaCampaignRuntimeConfig, campaignId: PetaCampaignId) {
  if (campaignId === PETA_CAMPAIGN_QA_ID) return config.qaAccessKey
  if (campaignId === PETA_CAMPAIGN_IN_PERSON_ID) return config.inPersonAccessKey
  return config.accessKey
}

function getCampaignIdForKey(key: string, config: PetaCampaignRuntimeConfig) {
  if (config.accessKey && constantTimeEqual(key, config.accessKey)) {
    return PETA_CAMPAIGN_ID
  }

  if (config.inPersonAccessKey && constantTimeEqual(key, config.inPersonAccessKey)) {
    return PETA_CAMPAIGN_IN_PERSON_ID
  }

  if (config.qaAccessKey && constantTimeEqual(key, config.qaAccessKey)) {
    return PETA_CAMPAIGN_QA_ID
  }

  return null
}

function getWindowState(config: PetaCampaignRuntimeConfig, campaignId: PetaCampaignId, now: Date) {
  const startIso = campaignId === PETA_CAMPAIGN_IN_PERSON_ID ? undefined : config.startIso
  const endIso = campaignId === PETA_CAMPAIGN_IN_PERSON_ID ? config.inPersonEndIso : config.endIso

  if (campaignId === PETA_CAMPAIGN_IN_PERSON_ID && !endIso) {
    return { active: false, end: null }
  }

  const start = parseDate(startIso)
  const end = parseDate(endIso)

  if ((startIso && !start) || (endIso && !end)) {
    return { active: false, end: null }
  }

  if ((start && now < start) || (end && now >= end)) {
    return { active: false, end }
  }

  return { active: true, end }
}

function getAccess(campaignId: PetaCampaignId, now: Date, campaignEnd: Date | null) {
  const maximumExpiry = now.getTime() + CAMPAIGN_COOKIE_LIFETIME_MS
  const expiresAt = campaignEnd ? Math.min(maximumExpiry, campaignEnd.getTime()) : maximumExpiry

  return {
    campaignId,
    expiresAt: new Date(expiresAt).toISOString(),
    attribution: getPetaCampaignAttribution(campaignId),
  } satisfies PetaCampaignAccess
}

export function evaluatePetaCampaignAccess({
  key,
  host,
  now = new Date(),
  config,
}: {
  key: string | null | undefined
  host: string
  now?: Date
  config: PetaCampaignRuntimeConfig
}): PetaCampaignEvaluation {
  const fallbackAttribution = getPetaCampaignAttribution(PETA_CAMPAIGN_ID)

  if (!key) {
    return { result: 'missing_key', attribution: fallbackAttribution, access: null }
  }

  if (!config.enabled) {
    return { result: 'disabled', attribution: fallbackAttribution, access: null }
  }

  const normalizedHost = normalizeHost(host)
  const allowed = config.allowedHosts.some(
    (allowedHost) => normalizeHost(allowedHost) === normalizedHost,
  )
  if (!allowed) {
    return { result: 'wrong_host', attribution: fallbackAttribution, access: null }
  }

  const campaignId = getCampaignIdForKey(key, config)
  if (!campaignId) {
    return { result: 'invalid', attribution: fallbackAttribution, access: null }
  }

  const attribution = getPetaCampaignAttribution(campaignId)
  const windowState = getWindowState(config, campaignId, now)
  if (!windowState.active) {
    return { result: 'outside_window', attribution, access: null }
  }

  const access = getAccess(campaignId, now, windowState.end)
  return { result: 'valid', attribution: access.attribution, access }
}

function bytesToBase64Url(bytes: Uint8Array) {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '')
}

function base64UrlToBytes(value: string) {
  const base64 = value.replaceAll('-', '+').replaceAll('_', '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  const binary = atob(padded)
  return Uint8Array.from(binary, (character) => character.charCodeAt(0))
}

async function importHmacKey(secret: string, usage: KeyUsage[]) {
  return crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    usage,
  )
}

export async function createPetaCampaignToken(
  access: PetaCampaignAccess,
  config: PetaCampaignRuntimeConfig,
) {
  const secret = getSigningKey(config, access.campaignId)
  if (!secret) throw new Error('Missing PETA campaign signing key')

  const payload: CampaignCookiePayload = {
    campaignId: access.campaignId,
    expiresAt: new Date(access.expiresAt).getTime(),
  }
  const encodedPayload = bytesToBase64Url(encoder.encode(JSON.stringify(payload)))
  const key = await importHmacKey(secret, ['sign'])
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(encodedPayload))

  return `${encodedPayload}.${bytesToBase64Url(new Uint8Array(signature))}`
}

export async function verifyPetaCampaignToken({
  token,
  now = new Date(),
  config,
}: {
  token: string | null | undefined
  now?: Date
  config: PetaCampaignRuntimeConfig
}): Promise<PetaCampaignAccess | null> {
  if (!token || !config.enabled) return null

  const [encodedPayload, encodedSignature, ...rest] = token.split('.')
  if (!encodedPayload || !encodedSignature || rest.length > 0) return null

  let payload: CampaignCookiePayload
  try {
    payload = JSON.parse(new TextDecoder().decode(base64UrlToBytes(encodedPayload)))
  } catch {
    return null
  }

  if (
    payload.campaignId !== PETA_CAMPAIGN_ID &&
    payload.campaignId !== PETA_CAMPAIGN_IN_PERSON_ID &&
    payload.campaignId !== PETA_CAMPAIGN_QA_ID
  ) {
    return null
  }
  if (!Number.isFinite(payload.expiresAt) || now.getTime() >= payload.expiresAt) return null

  const windowState = getWindowState(config, payload.campaignId, now)
  if (!windowState.active) return null

  const secret = getSigningKey(config, payload.campaignId)
  if (!secret) return null

  try {
    const key = await importHmacKey(secret, ['verify'])
    const verified = await crypto.subtle.verify(
      'HMAC',
      key,
      base64UrlToBytes(encodedSignature),
      encoder.encode(encodedPayload),
    )
    if (!verified) return null
  } catch {
    return null
  }

  const configuredEnd = windowState.end?.getTime()
  const expiresAt = configuredEnd ? Math.min(payload.expiresAt, configuredEnd) : payload.expiresAt

  return {
    campaignId: payload.campaignId,
    expiresAt: new Date(expiresAt).toISOString(),
    attribution: getPetaCampaignAttribution(payload.campaignId),
  }
}
